// ================================================================================
// ECOMSIMPLY - PRODUCT RESEARCH RESULTS COMPONENT
// Tableau des résultats de recherche avec score de bankabilité et métriques
// ================================================================================

import React from 'react';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  TrendingUp, 
  DollarSign, 
  Eye, 
  Star,
  AlertCircle,
  CheckCircle,
  Package,
  Building2
} from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';

const ProductResearchResults = ({ 
  results = [], 
  loading = false, 
  onProductClick,
  onSupplierClick 
}) => {
  
  const getBankabilityColor = (score) => {
    if (score >= 80) return 'bg-green-500 text-white';
    if (score >= 60) return 'bg-yellow-500 text-white';
    if (score >= 40) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const getBankabilityLabel = (score) => {
    if (score >= 80) return '🟢 EXCELLENT';
    if (score >= 60) return '🟡 BON';
    if (score >= 40) return '🟠 MOYEN';
    return '🔴 RISQUÉ';
  };

  const getCompetitionColor = (level) => {
    switch (level) {
      case 'very_low': return 'text-green-600 bg-green-50';
      case 'low': return 'text-yellow-600 bg-yellow-50';
      case 'medium': return 'text-orange-600 bg-orange-50';
      case 'high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getCompetitionLabel = (level) => {
    switch (level) {
      case 'very_low': return '🟢 Très faible';
      case 'low': return '🟡 Faible';
      case 'medium': return '🟠 Moyenne';
      case 'high': return '🔴 Élevée';
      default: return '⚪ Inconnue';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="grid grid-cols-6 gap-4">
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!results.length) {
    return (
      <Card className="p-8 text-center">
        <div className="text-gray-400 mb-4">
          <Package className="w-16 h-16 mx-auto mb-4" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucun produit trouvé
        </h3>
        <p className="text-gray-500">
          Essayez de modifier vos filtres de recherche pour obtenir des résultats.
        </p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Résultats de Recherche IA ({results.length} produits trouvés)
          </h2>
          <Badge variant="secondary">
            IA activée
          </Badge>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Produit
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score IA
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prix & Marge
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Concurrence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Métriques
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fournisseurs
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {results.map((product, index) => (
              <motion.tr
                key={product.asin || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                {/* Produit */}
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <img
                        className="h-12 w-12 rounded-lg object-cover"
                        src={product.image_url || '/api/placeholder/48/48'}
                        alt={product.title}
                        onError={(e) => {
                          e.target.src = '/api/placeholder/48/48';
                        }}
                      />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 line-clamp-2">
                        {product.title || 'Titre non disponible'}
                      </div>
                      <div className="text-sm text-gray-500">
                        ASIN: {product.asin || 'N/A'}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Score IA */}
                <td className="px-6 py-4">
                  <div className="flex flex-col items-start">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBankabilityColor(product.bankability_score || 0)}`}>
                      {product.bankability_score || 0}/100
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {getBankabilityLabel(product.bankability_score || 0)}
                    </div>
                  </div>
                </td>

                {/* Prix & Marge */}
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {product.current_price ? `${product.current_price}€` : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Marge: {product.finance?.margin_percent ? `${(product.finance.margin_percent * 100).toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-xs text-green-600">
                    ROI: {product.finance?.roi ? `${(product.finance.roi * 100).toFixed(1)}%` : 'N/A'}
                  </div>
                  <div className="text-xs text-blue-600">
                    COGS: {product.finance?.cogs_unit ? `${product.finance.cogs_unit.toFixed(2)}€` : 'N/A'}
                  </div>
                </td>

                {/* Concurrence */}
                <td className="px-6 py-4">
                  <Badge className={getCompetitionColor(product.competition_level)}>
                    {getCompetitionLabel(product.competition_level)}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">
                    Vendeurs: {product.seller_count || 'N/A'}
                  </div>
                </td>

                {/* Métriques */}
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center text-sm">
                      <Star className="w-3 h-3 text-yellow-400 mr-1" />
                      <span>{product.rating || 'N/A'} ({product.review_count || 0})</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      BSR: #{product.best_seller_rank || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Ventes/mois: {product.estimated_sales || 'N/A'}
                    </div>
                  </div>
                </td>

                {/* Fournisseurs */}
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900">
                      {product.supplier_count || 0} trouvés
                    </div>
                    <div className="text-xs text-gray-500">
                      Prix: {product.supplier_price_range || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      MOQ: {product.min_moq ? `${product.min_moq}+` : 'N/A'}
                    </div>
                    {product.has_verified_suppliers && (
                      <Badge variant="success" className="text-xs mt-1 w-fit">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Vérifiés
                      </Badge>
                    )}
                    {product.finance?.meets_target_margin && (
                      <Badge variant="success" className="text-xs mt-1 w-fit">
                        ✅ Marge cible
                      </Badge>
                    )}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onProductClick?.(product)}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Détails
                    </button>
                    <button
                      onClick={() => onSupplierClick?.(product)}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center"
                    >
                      <Building2 className="w-4 h-4 mr-1" />
                      Fournisseurs
                    </button>
                    {product.amazon_url && (
                      <a
                        href={product.amazon_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (si nécessaire) */}
      {results.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Affichage de {results.length} produits trouvés par l'IA
          </div>
        </div>
      )}
    </Card>
  );
};

export default ProductResearchResults;